import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  ApiGpdGpdsListRequest,
  ApiGpdReportsListRequest,
  ApiGpdVolumeSeriesListRequest,
  GPD,
  Report,
  VolumeSeries,
} from "@nens/brostar/openapi-client";

import { getApi } from "@/api/api-configuration";

import { fetchAllPages } from "./helpers/fetch-all-pages";
import { findInListQuery } from "./helpers/find-in-list-query";
import { queryDirectly } from "./helpers/query-directly";
import { simplifyQueryParam } from "./helpers/simplify-query-key";

const uploadApi = getApi();

// GPD StartRegistratie queries

// Ophalen enkele GPD
const getGpdSingleQuery = (uuid: string) => ({
  queryKey: ["gpd-single", uuid],
  queryFn: () => uploadApi.apiGpdGpdsRead({ uuid }),
});

// Enkele GPD ophalen op basis van list API, geeft andere informatie
export const useGpdSingleQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (uuid: string) =>
    findInListQuery<GPD>(queryClient, ["gpd-list"], uuid, getGpdSingleQuery(uuid));
};

// Ophalen lijst GPDs
const getGpdListQuery = (options: ApiGpdGpdsListRequest) => ({
  queryKey: ["gpd-list", simplifyQueryParam(options)],
  queryFn: () => uploadApi.apiGpdGpdsList(options),
  placeholderData: keepPreviousData,
});

export const useGpdListQuery = (options: ApiGpdGpdsListRequest) => {
  return useQuery(getGpdListQuery(options));
};

// Volume Series Queries -> Onderdeel van een rapport
// Er kunnen meerdere volume series aan een rapport gekoppeld zijn
// Binnen een GPD kunnen meerdere rapporten zitten.

const getGpdVolumeSeriesSingleQuery = (uuid: string) => ({
  queryKey: ["gpd-volume-series-single", uuid],
  queryFn: () => uploadApi.apiGpdVolumeSeriesRead({ uuid }),
});

const getGpdVolumeSeriesAllPagesQuery = ({ ...options }: ApiGpdVolumeSeriesListRequest) => ({
  queryKey: ["gpd-volume-series-all", simplifyQueryParam(options)],
  queryFn: () =>
    fetchAllPages((...pageOptions) =>
      uploadApi.apiGpdVolumeSeriesList({ ...options, ...pageOptions })
    ),
});

export const useGpdVolumeSeriesSingleQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (uuid: string) =>
    findInListQuery<VolumeSeries>(
      queryClient,
      ["gpd-volume-series-list"],
      uuid,
      getGpdVolumeSeriesSingleQuery(uuid)
    );
};

export const useGpdVolumeSeriesAllPagesQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (options: ApiGpdVolumeSeriesListRequest) =>
    queryDirectly(queryClient, getGpdVolumeSeriesAllPagesQuery(options));
};

export const useGpdVolumeSeriesListQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (options: ApiGpdVolumeSeriesListRequest) =>
    queryDirectly(queryClient, getGpdVolumeSeriesListQuery(options));
};

const getGpdVolumeSeriesListQuery = (options: ApiGpdVolumeSeriesListRequest) => ({
  queryKey: ["gpd-volume-series-list", simplifyQueryParam(options)],
  queryFn: () => uploadApi.apiGpdVolumeSeriesList(options),
  placeholderData: keepPreviousData,
});

export const useGpdVolumeSeriesListQuery = (options: ApiGpdVolumeSeriesListRequest) => {
  return useQuery(getGpdVolumeSeriesListQuery(options));
};

const getGpdVolumeSeriesListAllPagesQuery = () => ({
  queryKey: ["gpd-volume-series-list-all"],
  queryFn: () => fetchAllPages((pageOptions) => uploadApi.apiGpdVolumeSeriesList(pageOptions)),
});

export const useGpdVolumeSeriesListAllPagesQuery = () => {
  return useQuery(getGpdVolumeSeriesListAllPagesQuery());
};

// GPD Rapport queries
// Rapporten zijn de 'aanvullingen' van een GPD registratie
// Hierin staat vrijwel alle nuttige informatie
// Onder het rapport vallen de Volume Series van hierboven.

const getGpdReportSingleQuery = (uuid: string) => ({
  queryKey: ["gpd-report-single", uuid],
  queryFn: () => uploadApi.apiGpdReportsRead({ uuid }),
});

export const useGpdReportSingleQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (uuid: string) =>
    findInListQuery<Report>(queryClient, ["gpd-report-list"], uuid, getGpdReportSingleQuery(uuid));
};

const getGpdReportListQuery = (options: ApiGpdReportsListRequest) => ({
  queryKey: ["gpd-report-list", simplifyQueryParam(options)],
  queryFn: () => uploadApi.apiGpdReportsList(options),
  placeholderData: keepPreviousData,
});

export const useGpdReportListQuery = (options: ApiGpdReportsListRequest) => {
  return useQuery(getGpdReportListQuery(options));
};

const getGpdReportListAllPagesQuery = () => ({
  queryKey: ["gpd-report-list-all"],
  queryFn: () => fetchAllPages((pageOptions) => uploadApi.apiGpdReportsList(pageOptions)),
});

export const useGpdReportListAllPagesQuery = () => {
  return useQuery(getGpdReportListAllPagesQuery());
};

// Invalideren queries

export const useInvalidateGpdQueries = () => {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ["gpd-single"],
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: getGpdListQuery({}).queryKey.slice(0, -1),
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: ["gpd-report-single"],
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: getGpdReportListQuery({}).queryKey.slice(0, -1),
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: getGpdReportListAllPagesQuery().queryKey,
      refetchType: "all",
    });
  }, [queryClient]);
};
